'use client'

import * as React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatDate = (value) => {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
}

const getIndicatorTarget = (indicator) => {
  if (Array.isArray(indicator.targets) && indicator.targets.length > 0) {
    return parseAmount(indicator.targets[0]?.value)
  }
  return parseAmount(indicator.target || indicator.targetValue || 0)
}

const getIndicatorActual = (indicator) =>
  parseAmount(
    indicator.actual ??
      indicator.currentValue ??
      indicator.current ??
      indicator.value ??
      indicator.progress ??
      0,
  )

const getIndicatorRate = (indicator) => {
  const target = getIndicatorTarget(indicator) || 0
  const actual = getIndicatorActual(indicator)
  if (!target) return 0
  return Math.round((actual / target) * 100)
}

export default function MELDashboard() {
  const [indicators, setIndicators] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [evidenceRecords, setEvidenceRecords] = React.useState([])
  const [indicatorStats, setIndicatorStats] = React.useState({
    total: 0,
    onTrack: 0,
    atRisk: 0,
    offTrack: 0,
  })
  const [statsLoading, setStatsLoading] = React.useState(true)
  const [taskBoards, setTaskBoards] = React.useState({
    not_started: [],
    in_progress: [],
    completed: [],
    delayed: [],
  })
  const [taskBoardsLoading, setTaskBoardsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadIndicatorStats() {
      try {
        const [deliverableSnap, projectSnap, indicatorSnap] = await Promise.all([
          getDocs(collection(db, 'deliverables')),
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'indicators')),
        ])
        if (!isMounted) return
        const deliverables = deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const indicatorList = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projectMap = projects.reduce((acc, project) => {
          acc[project.id] = project
          return acc
        }, {})
        setIndicators(indicatorList)
        setProjects(projects)
        let onTrack = 0
        let atRisk = 0
        let offTrack = 0

        const validStatuses = new Set(['completed', 'in_progress', 'in-progress', 'delayed', 'not_started', 'not-started'])
        const grouped = {
          not_started: [],
          in_progress: [],
          completed: [],
          delayed: [],
        }
        deliverables.forEach((item) => {
          const status = String(item.status || '').toLowerCase()
          if (!validStatuses.has(status)) return
          if (status === 'completed' || status === 'in_progress' || status === 'in-progress') onTrack += 1
          else if (status === 'delayed') atRisk += 1
          else offTrack += 1

          const normalized = status.replace(/-/g, '_')
          if (!grouped[normalized]) return
          const project = projectMap[item.projectId]
          grouped[normalized].push({
            id: item.id,
            name: item.name || item.title || 'Task',
            projectCode: project?.code || project?.shortCode || project?.title || item.projectId || 'Project',
            owner: item.leadPersonName || item.responsiblePersonName || 'Unassigned',
            dueDate: item.plannedEnd,
          })
        })

        setIndicatorStats({
          total: onTrack + atRisk + offTrack,
          onTrack,
          atRisk,
          offTrack,
        })
        setTaskBoards(grouped)
      } catch {
        if (!isMounted) return
        setIndicatorStats({ total: 0, onTrack: 0, atRisk: 0, offTrack: 0 })
        setTaskBoards({
          not_started: [],
          in_progress: [],
          completed: [],
          delayed: [],
        })
        setIndicators([])
        setProjects([])
      } finally {
        if (isMounted) {
          setStatsLoading(false)
          setTaskBoardsLoading(false)
        }
      }
    }

    loadIndicatorStats()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadEvidence() {
      try {
        const indicatorSnap = await getDocs(collection(db, 'indicators'))
        if (!isMounted) return
        const indicatorList = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const evidence = indicatorList
          .filter((indicator) => indicator.evidence?.url)
          .map((indicator) => ({
            id: indicator.id,
            verified: Boolean(indicator.evidence?.verified),
          }))
        setEvidenceRecords(evidence)
      } catch {
        if (!isMounted) return
        setEvidenceRecords([])
      }
    }

    loadEvidence()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const indicatorsByProject = indicators.reduce((acc, indicator) => {
    if (!indicator.projectId) return acc
    if (!acc[indicator.projectId]) acc[indicator.projectId] = []
    acc[indicator.projectId].push(indicator)
    return acc
  }, {})

  const achievementByProject = projects.map((project) => {
    const projectIndicators = indicatorsByProject[project.id] || []
    const avg = projectIndicators.length
      ? Math.round(projectIndicators.reduce((sum, item) => sum + getIndicatorRate(item), 0) / projectIndicators.length)
      : 0
    return {
      project: project.code || project.shortCode || project.title || 'Project',
      achievement: clamp(avg, 0, 100),
    }
  })

  const indicatorLevels = indicators.reduce((acc, indicator) => {
    const level = String(indicator.level || indicator.levelName || '').toLowerCase()
    if (!level) return acc
    if (!acc[level]) acc[level] = []
    acc[level].push(indicator)
    return acc
  }, {})

  const levelData = ['goal', 'outcome', 'output', 'impact'].map((level) => {
    const items = indicatorLevels[level] || []
    const avg = items.length ? Math.round(items.reduce((sum, item) => sum + getIndicatorRate(item), 0) / items.length) : 0
    return { level: level.charAt(0).toUpperCase() + level.slice(1), count: items.length, avg: clamp(avg, 0, 100) }
  })

  const verifiedCount = evidenceRecords.filter((item) => item.verified).length
  const pendingCount = evidenceRecords.filter((item) => !item.verified).length
  const evidenceStatusPie = [
    { name: 'Verified', value: verifiedCount, color: 'oklch(0.55 0.16 150)' },
    { name: 'Pending', value: pendingCount, color: 'oklch(0.7 0.17 65)' },
  ]

  const indicatorBarData = indicators
    .map((indicator) => ({
      name: (indicator.name || 'Indicator').length > 20 ? `${indicator.name.slice(0, 20)}...` : indicator.name || 'Indicator',
      target: getIndicatorTarget(indicator),
      actual: getIndicatorActual(indicator),
      rate: getIndicatorRate(indicator),
    }))
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8)

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">MEL Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitoring, Evaluation, and Learning performance at a glance</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{statsLoading ? '—' : indicatorStats.total}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Total Indicators</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{statsLoading ? '—' : indicatorStats.onTrack}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">On Track</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-warning">{statsLoading ? '—' : indicatorStats.atRisk}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">At Risk</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{statsLoading ? '—' : indicatorStats.offTrack}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Off Track</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{statsLoading ? '—' : evidenceRecords.length}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Evidence Items</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
          </CardContent>
        </Card>
      </div>

      {/* Indicators Task Boards */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">Indicators Task Board</CardTitle>
          <CardDescription className="text-xs">
            Tasks pulled from all project workplans
          </CardDescription>
        </CardHeader>
        <CardContent>
          {taskBoardsLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading tasks...</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { key: 'not_started', label: 'Not Started' },
                { key: 'in_progress', label: 'In Progress' },
                { key: 'completed', label: 'Completed' },
                { key: 'delayed', label: 'Delayed' },
              ].map((col) => {
                const tasks = taskBoards[col.key] || []
                return (
                  <Card key={col.key} className="border border-border">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-xs font-semibold text-foreground">{col.label}</CardTitle>
                        <Badge variant="secondary" className="text-[10px]">{tasks.length}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {tasks.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-3 text-center text-[10px] text-muted-foreground">
                          No tasks
                        </div>
                      ) : (
                        tasks.slice(0, 6).map((task) => (
                          <div key={task.id} className="rounded-lg border bg-card p-3">
                            <p className="text-xs font-semibold text-foreground truncate">{task.name}</p>
                            <p className="text-[10px] text-muted-foreground mt-1 truncate">
                              {task.projectCode}
                            </p>
                            <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                              <span className="truncate">{task.owner}</span>
                              <span>{formatDate(task.dueDate)}</span>
                            </div>
                          </div>
                        ))
                      )}
                      {tasks.length > 6 ? (
                        <p className="text-[10px] text-muted-foreground">+{tasks.length - 6} more</p>
                      ) : null}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Achievement by Project</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={achievementByProject}>
                <PolarGrid stroke="oklch(0.9 0.01 145)" />
                <PolarAngleAxis dataKey="project" tick={{ fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
                <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                <Radar dataKey="achievement" stroke="oklch(0.35 0.12 145)" fill="oklch(0.35 0.12 145)" fillOpacity={0.3} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Performance by Level</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={levelData} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="level" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" unit="%" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="avg" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} name="Avg Achievement %" label={{ position: 'top', fontSize: 10, fill: 'oklch(0.48 0.03 145)' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Evidence Verification</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={evidenceStatusPie} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={4} strokeWidth={0}>
                  {evidenceStatusPie.map(e => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4">
              {evidenceStatusPie.map(e => (
                <div key={e.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: e.color }} />
                  <span className="text-[10px] text-muted-foreground">
                    {e.name} ({e.value})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Target vs Actual Bar */}
      <Card className="border border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Target vs Actual: Top Indicators</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={indicatorBarData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} stroke="oklch(0.48 0.03 145)" width={160} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="target" fill="oklch(0.35 0.12 145)" name="Target (Y1)" radius={[0, 3, 3, 0]} />
              <Bar dataKey="actual" fill="oklch(0.87 0.15 85)" name="Actual" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  )
}
